import { useQuery } from '@tanstack/react-query';
import { announcementApi } from '../utils/api';
import { Megaphone, Calendar, Pin, ChevronRight } from 'lucide-react';
import { useState } from 'react';

interface Announcement {
  id: number;
  title: string;
  content: string;
  priority: number;
  is_pinned: boolean;
  start_date?: string;
  end_date?: string;
  created_at: string;
}

export default function Announcements() {
  const [selectedAnnounce, setSelectedAnnounce] = useState<Announcement | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['announcements'],
    queryFn: async () => {
      const response = await announcementApi.getActive();
      return response.data.announcements as Announcement[];
    },
  });

  const sortedAnnouncements = data?.sort((a: Announcement, b: Announcement) => {
    if (a.is_pinned && !b.is_pinned) return -1;
    if (!a.is_pinned && b.is_pinned) return 1;
    return b.priority - a.priority;
  });

  const getPriorityColor = (priority: number) => {
    switch (priority) {
      case 3: return 'badge-error';
      case 2: return 'badge-warning';
      default: return 'badge-info';
    }
  };

  const getPriorityLabel = (priority: number) => {
    switch (priority) {
      case 3: return '重要';
      case 2: return '公告';
      default: return '一般';
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-6">
        <Megaphone className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-bold">公告</h1>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <span className="loading loading-spinner loading-lg"></span>
        </div>
      ) : sortedAnnouncements?.length === 0 ? (
        <div className="text-center py-12">
          <Megaphone className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">暫無公告</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedAnnouncements?.map((announce) => (
            <div
              key={announce.id}
              className={`card bg-base-100 shadow hover:shadow-md transition-shadow cursor-pointer ${
                announce.is_pinned ? 'border-l-4 border-primary' : ''
              }`}
              onClick={() => setSelectedAnnounce(announce)}
            >
              <div className="card-body">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {announce.is_pinned && (
                        <Pin className="h-4 w-4 text-primary" />
                      )}
                      <span className={`badge ${getPriorityColor(announce.priority)}`}>
                        {getPriorityLabel(announce.priority)}
                      </span>
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(announce.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <h3 className={`text-lg font-bold ${announce.is_pinned ? 'text-primary' : ''}`}>
                      {announce.title}
                    </h3>
                    <p className="text-gray-600 line-clamp-2 mt-1">{announce.content}</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {selectedAnnounce && (
        <div className="modal modal-open">
          <div className="modal-box max-w-3xl">
            <div className="flex items-center gap-2 mb-4">
              {selectedAnnounce.is_pinned && <Pin className="h-5 w-5 text-primary" />}
              <span className={`badge ${getPriorityColor(selectedAnnounce.priority)}`}>
                {getPriorityLabel(selectedAnnounce.priority)}
              </span>
            </div>
            <h2 className="text-2xl font-bold mb-4">{selectedAnnounce.title}</h2>
            <div className="prose max-w-none mb-4 whitespace-pre-wrap">{selectedAnnounce.content}</div>
            <div className="text-sm text-gray-400 flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              發布時間: {new Date(selectedAnnounce.created_at).toLocaleString()}
            </div>
            <div className="modal-action">
              <button className="btn" onClick={() => setSelectedAnnounce(null)}>
                關閉
              </button>
            </div>
          </div>
          <div className="modal-backdrop" onClick={() => setSelectedAnnounce(null)}></div>
        </div>
      )}
    </div>
  );
}
